<template>
  <div
    class="absolute right-0 top-[45px] mt-2 z-50 w-80 bg-white shadow-xl border border-border overflow-hidden overscroll-contain"
    @click.stop
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-border">
      <div class="flex items-center gap-2">
        <span class="text-sm font-semibold text-text-primary">Cart</span>
        <span
          v-if="items.length > 0"
          class="flex items-center justify-center min-w-[1.25rem] h-5 w-5 rounded-full bg-primary text-white text-[10px] font-bold"
        >
          {{ items.length }}
        </span>
      </div>
      <button
        type="button"
        class="flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-text-primary hover:bg-gray-200 transition-colors"
        aria-label="Close cart"
        @click="$emit('close')"
      >
        <PhX :size="14" weight="bold" />
      </button>
    </div>

    <!-- Empty state -->
    <div v-if="items.length === 0" class="py-8 text-center">
      <PhShoppingCart :size="36" weight="light" class="mx-auto text-text-muted mb-2" />
      <p class="text-sm text-text-secondary m-0">Your cart is empty</p>
    </div>

    <!-- Item list -->
    <div v-else class="overflow-y-auto max-h-64 px-4 py-3 flex flex-col gap-2">
      <div
        v-for="(item, index) in items"
        :key="`${item.item_id}-${item.attribute_id}-${index}`"
        class="flex items-start justify-between gap-3"
      >
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-text-primary m-0 truncate">{{ item.item_name }}</p>
          <p class="text-xs text-text-secondary m-0 mt-0.5">
            Qty {{ item.count }}
            <span v-if="item.attribute_name"> · {{ item.attribute_name }}</span>
          </p>
        </div>
        <span class="text-sm font-semibold text-text-primary shrink-0">
          {{ formatRs(item.total_amount ?? item.item_amount ?? 0) }}
        </span>
      </div>
    </div>

    <!-- Footer: total + button -->
    <div v-if="items.length > 0" class="border-t border-border px-4 py-3">
      <div class="flex items-center justify-between mb-3">
        <span class="text-sm text-text-secondary">Total</span>
        <span class="text-base font-bold text-primary">{{ formatRs(total) }}</span>
      </div>
      <button
        type="button"
        class="w-full bg-primary text-white text-sm font-semibold py-2.5 rounded-xl transition-opacity hover:opacity-90 border-none cursor-pointer"
        @click="$emit('open-cart')"
      >
        View Cart &amp; Place Order
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PhX, PhShoppingCart } from '@phosphor-icons/vue'
import { useCurrency } from '@/composables/core/useCurrency'
import type { OrderItem } from '@/interfaces'

interface Props {
  items: OrderItem[]
  total: number
}

defineProps<Props>()

defineEmits<{
  close: []
  'open-cart': []
}>()

const { formatDefaultPrice: formatRs } = useCurrency()
</script>
